import { useCallback, useEffect, useRef, useState } from "react";
import type { MobileRoute } from "./types";

const routes: MobileRoute[] = ["home", "games", "playing", "promotions", "support", "support-chat", "messages", "account", "wallet", "deposit", "withdraw", "cards", "coin-guide", "funds", "game-records", "vip", "rebate-rules", "password", "referrals", "settings", "member-profile", "security"];
export const routeNames: Record<MobileRoute, string> = { settings: "设置", "member-profile": "个人资料", security: "安全中心", password: "修改密码", referrals: "合营中心", home: "首页", games: "游戏", playing: "游戏", promotions: "活动", support: "客服", "support-chat": "在线客服", messages: "消息中心", account: "我的", wallet: "我的钱包", deposit: "充值", withdraw: "提现", cards: "银行卡", "coin-guide": "购币指南", funds: "资金管理", "game-records": "游戏管理", vip: "VIP 等级", "rebate-rules": "返水比例详情" };

export function readMobileLocation() {
  const [name, search = ""] = window.location.hash.slice(1).split("?");
  return { route: (routes.includes(name as MobileRoute) ? name : "home") as MobileRoute, params: new URLSearchParams(search), key: window.location.hash };
}

export function useMobileNavigation() {
  const [location, setLocation] = useState(readMobileLocation);
  const guard = useRef({ dirty: false, busy: false });
  const [pending, setPending] = useState<(() => void) | null>(null);
  const lastUrl = useRef(window.location.href);
  const lastIndex = useRef(Number(window.history.state?.xhIndex ?? 0));
  const reverting = useRef(false);
  const allowNext = useRef(false);
  const setGuard = useCallback((value: { dirty: boolean; busy: boolean }) => { guard.current = value; }, []);

  const commit = useCallback(() => {
    lastUrl.current = window.location.href;
    lastIndex.current = Number(window.history.state?.xhIndex ?? 0);
    setLocation(readMobileLocation());
  }, []);

  useEffect(() => {
    window.history.replaceState({ ...window.history.state, xhIndex: lastIndex.current }, "");
    const changed = () => {
      if (reverting.current) { reverting.current = false; return; }
      if (lastUrl.current === window.location.href) return;
      if (allowNext.current) { allowNext.current = false; commit(); return; }
      if (guard.current.dirty || guard.current.busy) {
        const target = window.location.href;
        const targetIndex = Number(window.history.state?.xhIndex ?? lastIndex.current - 1);
        const delta = lastIndex.current - targetIndex;
        if (delta) { reverting.current = true; window.history.go(delta); }
        else window.history.replaceState({ xhIndex: lastIndex.current }, "", lastUrl.current);
        if (!guard.current.busy) setPending(() => () => {
          guard.current = { dirty: false, busy: false };
          if (delta) { allowNext.current = true; window.history.go(-delta); }
          else { window.history.pushState({ xhIndex: lastIndex.current + 1 }, "", target); commit(); }
        });
        return;
      }
      commit();
    };
    window.addEventListener("popstate", changed);
    window.addEventListener("hashchange", changed);
    return () => { window.removeEventListener("popstate", changed); window.removeEventListener("hashchange", changed); };
  }, [commit]);

  const navigate = useCallback((route: MobileRoute, params?: Record<string, string>, options?: { replace?: boolean }) => {
    const search = new URLSearchParams(params).toString();
    const hash = route === "home" ? "" : `#${route}${search ? `?${search}` : ""}`;
    const destination = `${window.location.pathname}${window.location.search}${hash}`;
    const apply = () => {
      if (window.location.hash !== hash) {
        if (options?.replace) window.history.replaceState({ xhIndex: lastIndex.current }, "", destination);
        else window.history.pushState({ xhIndex: lastIndex.current + 1 }, "", destination);
      }
      commit();
      window.scrollTo({ top: 0, behavior: "instant" });
    };
    if (guard.current.busy) return;
    if (guard.current.dirty) setPending(() => apply); else apply();
  }, [commit]);

  const back = useCallback(() => { if (lastIndex.current > 0) window.history.back(); else navigate("account"); }, [navigate]);
  const resolvePending = useCallback((discard: boolean) => { const apply = pending; setPending(null); if (discard) { guard.current = { dirty: false, busy: false }; apply?.(); } }, [pending]);
  return { ...location, navigate, back, setGuard, pending, resolvePending };
}
